import type { ComponentProps } from 'react';

import { Tabs, TabsList, TabsPanel, TabsTab } from '@vs-shop/ui/tabs';
import { clsxm } from '@vs-shop/utils';

/**
 * What else a product goes with, sorted into groups a shopper picks between.
 *
 *   <ProductAccessories value={current} onValueChange={choose}>
 *     <ProductAccessoriesTabList>
 *       <ProductAccessoriesTab value="all">
 *         <ProductAccessoriesTabLabel>Vše</ProductAccessoriesTabLabel>
 *       </ProductAccessoriesTab>
 *       …
 *     </ProductAccessoriesTabList>
 *
 *     <ProductAccessoriesPanel value="all">
 *       {grid}
 *       <Pagination>…</Pagination>
 *       <PaginationLoadMore>…</PaginationLoadMore>
 *     </ProductAccessoriesPanel>
 *     …
 *   </ProductAccessories>
 *
 * A rail of groups beside the products in the one that is current. Which groups
 * exist, which is current, what fills the panel and where the next page comes
 * from are all the tenant's — these parts are the rail, the places on it, and
 * the box beside it.
 *
 * **The grid inside the panel is not here.** What a page shows for a group is
 * whatever it composes into the panel: rcprofi puts its own card grid, its
 * pagination and its load-more control there, all of which already exist as
 * shared parts of their own (`@vs-shop/commerce-ui/productCard`,
 * `…/pagination`). A section that also owned the grid would be a second way to
 * lay cards out.
 *
 * **This is a local cut, not a platform contract.** `SPEC.md` leaves the shape
 * of context-backed interactive compounds deliberately uncodified, and this is
 * one section's answer to it: the machinery is `@vs-shop/ui`'s tabs primitive,
 * the state is the caller's through `value`/`onValueChange`, and no part takes
 * a "current" prop — Base UI marks the current tab `data-active` and the recipe
 * paints off that mark. Nothing binds the next interactive section to it.
 *
 * The rail is a vertical list at every width, so the root declares itself
 * vertical: arrow-up and arrow-down move along it, which is the direction a
 * shopper sees. No part renders a heading — what the block is called is a
 * neutral element the page composes above it.
 */

/**
 * The block: a rail and the panel it drives, side by side where there is room.
 *
 * Below `lap` the two stack, and the rail is a full-width list above the
 * products rather than beside them. That step is the section's, not a
 * composition's — a tenant wanting one or the other at every width overrides
 * the flex direction through `className`.
 */
const productAccessoriesClasses = 'lap:flex-row flex flex-col gap-4';

export type ProductAccessoriesProps = Omit<ComponentProps<typeof Tabs>, 'onValueChange'> & {
  /**
   * Which group a shopper asked for.
   *
   * Narrowed here rather than at every call site: the primitive types a tab's
   * value as whatever the caller registered, and every group on this rail is
   * addressed by a string. A tenant otherwise repeats the same guard in its own
   * JSX, and so does every fixture and story beside it.
   */
  readonly onValueChange?: (value: string) => void;
};

/** The accessory block. */
export const ProductAccessories = ({
  className,
  onValueChange,
  ...props
}: ProductAccessoriesProps) => (
  <Tabs
    orientation="vertical"
    data-slot="product-accessories"
    className={clsxm(productAccessoriesClasses, className)}
    onValueChange={(value: unknown) => {
      if (typeof value === 'string') {
        onValueChange?.(value);
      }
    }}
    {...props}
  />
);

/**
 * The rail of groups.
 *
 * `h-max` is what keeps it from stretching to the panel's height beside it: a
 * rail of four groups next to a grid of eight cards would otherwise draw its
 * own edge all the way down the page.
 */
const productAccessoriesTabListClasses =
  'divide-foreground/20 border-foreground/20 lap:w-1/4 flex h-max w-full flex-col divide-y ' +
  'overflow-hidden rounded-xl border';

export type ProductAccessoriesTabListProps = ComponentProps<typeof TabsList>;

/** The list of groups. */
export const ProductAccessoriesTabList = ({
  className,
  ...props
}: ProductAccessoriesTabListProps) => (
  <TabsList
    data-slot="product-accessories-tab-list"
    className={clsxm(productAccessoriesTabListClasses, className)}
    {...props}
  />
);

/**
 * One group on the rail.
 *
 * Which one is current is read off the DOM rather than passed: the primitive
 * marks it `data-active`, so the inverted arm below is a CSS rule rather than a
 * class computed per render — correct at first paint on the server, and
 * unaffected by a tenant reordering the rail.
 *
 * The hover arm is guarded with `not-data-active:` rather than left to
 * ordering. Both arms set a background, and the one a shopper's pointer is over
 * is exactly the case where they would otherwise both apply.
 */
const productAccessoriesTabClasses =
  'text-foreground bg-background-intense flex items-center justify-between px-8 py-6 ' +
  'text-left transition-colors ' +
  'not-data-active:hover:bg-background ' +
  'data-active:bg-background-inverted data-active:text-foreground-inverted';

export type ProductAccessoriesTabProps = ComponentProps<typeof TabsTab>;

/** A group's place on the rail. */
export const ProductAccessoriesTab = ({ className, ...props }: ProductAccessoriesTabProps) => (
  <TabsTab
    data-slot="product-accessories-tab"
    className={clsxm(productAccessoriesTabClasses, className)}
    {...props}
  />
);

/**
 * What a group is called.
 *
 * Capped at one line: a rail whose width is a quarter of the page cannot let a
 * long group name set its own height, and a group with no name of its own is a
 * code the tenant falls back to, which is longer still.
 */
const productAccessoriesTabLabelClasses = 'line-clamp-1';

export type ProductAccessoriesTabLabelProps = ComponentProps<'span'>;

/** The group's name. */
export const ProductAccessoriesTabLabel = ({
  className,
  ...props
}: ProductAccessoriesTabLabelProps) => (
  <span
    data-slot="product-accessories-tab-label"
    className={clsxm(productAccessoriesTabLabelClasses, className)}
    {...props}
  />
);

/**
 * What one group holds.
 *
 * A column rather than a bare box, because a page puts more than a grid in
 * here — rcprofi follows its cards with a pager and a load-more control, and
 * the spacing between the three is the panel's.
 */
const productAccessoriesPanelClasses = 'lap:w-3/4 flex w-full flex-col gap-6';

export type ProductAccessoriesPanelProps = ComponentProps<typeof TabsPanel>;

/** The products in the current group. */
export const ProductAccessoriesPanel = ({ className, ...props }: ProductAccessoriesPanelProps) => (
  <TabsPanel
    data-slot="product-accessories-panel"
    className={clsxm(productAccessoriesPanelClasses, className)}
    {...props}
  />
);
